import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Calendar as CalendarIcon, Wallet, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { upsertCouple } from '../api';
import { useAuth, nextRoute } from '../context/AuthContext';

export default function Onboarding() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, setCouple, setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ brideName: '', groomName: '', akadDate: '', resepsiDate: '', venue: '', totalBudget: '' });

  const next = () => {
    if (step === 1 && (!form.brideName || !form.groomName)) { toast({ title: 'Isi nama pasangan dulu', variant: 'destructive' }); return; }
    if (step === 2 && (!form.akadDate)) { toast({ title: 'Pilih tanggal akad dulu', variant: 'destructive' }); return; }
    setStep(step + 1);
  };

  const finish = async () => {
    if (!form.totalBudget) { toast({ title: 'Isi target budget', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const payload = { ...form, totalBudget: Number(form.totalBudget) };
      const saved = await upsertCouple(payload);
      setCouple(saved);
      toast({ title: 'Selamat! Data pernikahanmu tersimpan 🎉' });
      const updated = { ...user, onboarded: true };
      setUser(updated);
      navigate(nextRoute(updated));
    } catch (err) {
      toast({ title: 'Gagal menyimpan', description: err?.response?.data?.detail || 'Coba lagi', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-cream-100 flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-full bg-terracotta-500 flex items-center justify-center">
            <Heart className="w-5 h-5 text-white" fill="white" />
          </div>
          <div className="font-serif text-xl text-sage-700">Naik Pelaminan</div>
        </div>

        <div className="flex items-center justify-center gap-2 mb-6">
          {[1,2,3].map(s => (
            <div key={s} className={`h-1.5 rounded-full transition-all ${s === step ? 'w-8 bg-terracotta-500' : s < step ? 'w-4 bg-terracotta-300' : 'w-4 bg-cream-200'}`} />
          ))}
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-sm border border-cream-200">
          {step === 1 && (
            <div className="slide-up">
              <div className="w-14 h-14 rounded-2xl bg-terracotta-50 flex items-center justify-center mb-4">
                <Heart className="w-7 h-7 text-terracotta-500" fill="currentColor" />
              </div>
              <h1 className="font-serif text-3xl text-sage-700">Siapa pasangan bahagianya?</h1>
              <p className="text-sage-500 text-sm mt-1 mb-6">Nama kalian akan muncul di dashboard.</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sage-600 text-sm">Mempelai wanita</Label>
                  <Input data-testid="onboarding-bride-input" value={form.brideName} onChange={(e) => setForm({...form, brideName: e.target.value})} placeholder="Suci" className="mt-1.5 h-11 bg-cream-50 border-cream-200 rounded-xl" />
                </div>
                <div>
                  <Label className="text-sage-600 text-sm">Mempelai pria</Label>
                  <Input data-testid="onboarding-groom-input" value={form.groomName} onChange={(e) => setForm({...form, groomName: e.target.value})} placeholder="Dimas" className="mt-1.5 h-11 bg-cream-50 border-cream-200 rounded-xl" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="slide-up">
              <div className="w-14 h-14 rounded-2xl bg-terracotta-50 flex items-center justify-center mb-4">
                <CalendarIcon className="w-7 h-7 text-terracotta-500" />
              </div>
              <h1 className="font-serif text-3xl text-sage-700">Kapan hari-H nya?</h1>
              <p className="text-sage-500 text-sm mt-1 mb-6">Kami akan hitung mundur untukmu.</p>
              <div className="space-y-4">
                <div>
                  <Label className="text-sage-600 text-sm">Tanggal akad</Label>
                  <Input data-testid="onboarding-akad-date-input" type="date" value={form.akadDate} onChange={(e) => setForm({...form, akadDate: e.target.value})} className="mt-1.5 h-11 bg-cream-50 border-cream-200 rounded-xl" />
                </div>
                <div>
                  <Label className="text-sage-600 text-sm">Tanggal resepsi (opsional)</Label>
                  <Input data-testid="onboarding-resepsi-date-input" type="date" value={form.resepsiDate} onChange={(e) => setForm({...form, resepsiDate: e.target.value})} className="mt-1.5 h-11 bg-cream-50 border-cream-200 rounded-xl" />
                </div>
                <div>
                  <Label className="text-sage-600 text-sm">Venue (opsional)</Label>
                  <Input data-testid="onboarding-venue-input" value={form.venue} onChange={(e) => setForm({...form, venue: e.target.value})} placeholder="Gedung / lokasi" className="mt-1.5 h-11 bg-cream-50 border-cream-200 rounded-xl" />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="slide-up">
              <div className="w-14 h-14 rounded-2xl bg-terracotta-50 flex items-center justify-center mb-4">
                <Wallet className="w-7 h-7 text-terracotta-500" />
              </div>
              <h1 className="font-serif text-3xl text-sage-700">Target budget?</h1>
              <p className="text-sage-500 text-sm mt-1 mb-6">Tenang, bisa diubah kapan aja nanti.</p>
              <div>
                <Label className="text-sage-600 text-sm">Total budget pernikahan</Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sage-500">Rp</span>
                  <Input data-testid="onboarding-budget-input" type="number" value={form.totalBudget} onChange={(e) => setForm({...form, totalBudget: e.target.value})} placeholder="70000000" className="pl-10 h-11 bg-cream-50 border-cream-200 rounded-xl" />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {[50000000, 70000000, 100000000, 150000000, 200000000].map(v => (
                    <button key={v} type="button" onClick={() => setForm({...form, totalBudget: v})} className="px-3 py-1.5 text-xs rounded-full bg-cream-100 hover:bg-terracotta-50 text-sage-600 border border-cream-200">
                      Rp {(v/1000000).toFixed(0)}jt
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <Button data-testid="onboarding-back-button" variant="outline" onClick={() => setStep(step - 1)} className="h-11 rounded-full border-cream-200 text-sage-600">
                <ChevronLeft className="w-4 h-4 mr-1" /> Kembali
              </Button>
            )}
            {step < 3 ? (
              <Button data-testid="onboarding-next-button" onClick={next} className="flex-1 h-11 bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-full">
                Lanjut <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button data-testid="onboarding-finish-button" onClick={finish} disabled={busy} className="flex-1 h-11 bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-full">
                {busy ? 'Menyimpan...' : 'Mulai persiapan ✨'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
